import { useCallback } from 'react';
import { Counter } from '../../components/Counter';
import { DatePicker } from '../DatePicker';
import { GuestPicker } from '../../components/GuestPicker';
import {
  CounterLabel,
  CounterRow,
  Dropdown as GuestDropdown,
} from '../../components/GuestPicker/GuestPicker.styles';
import { useDropdownGroup } from '../../hooks/useDropdownGroup';
import { CategorySearchIcon } from '../../icons/CategorySearchIcon';
import { ChevronDownIcon } from '../../icons/ChevronDownIcon';
import { DotIcon } from '../../icons/DotIcon';
import { SearchSmallIcon } from '../../icons/SearchSmallIcon';
import {
  CategoryOption,
  CategoryThumb,
  CategoryThumbImg,
  ChevronIcon,
  PillIcon,
} from './JourneyPicker.styles';
import {
  Bar,
  CategoryDropdown,
  CategoryWrapper,
  ClearRateButton,
  DatesWrapper,
  DotSeparator,
  DropdownAnchor,
  GuestsWrapper,
  Pill,
  RateDropdownShell,
  RateLabelText,
  RatePillGroup,
  RatePillLabelButton,
  RateWrapper,
  SearchPill,
  SearchWrapper,
} from './JourneyPickerDesktop.styles';
import { RateCodeField } from './RateCodeField';
import type { JourneyPickerSharedProps } from './JourneyPickerView.types';

const DROPDOWN_KEYS = ['category', 'dates', 'guests', 'rate'] as const;
type DropdownKey = (typeof DROPDOWN_KEYS)[number];

export interface JourneyPickerDesktopProps extends JourneyPickerSharedProps {
  columnView?: boolean;
  numberOfMonths?: 1 | 2;
}

export function JourneyPickerDesktop({
  showCategory,
  categories,
  category,
  currentCategoryLabel,
  onCategorySelect,
  fromDate,
  toDate,
  isSingleDate,
  datesLabel,
  onRangeSelect,
  displayGuests,
  guestMode,
  adults,
  children,
  guestsLabel,
  onAdultsChange,
  onChildrenChange,
  availability,
  isLoadingAvailability,
  onMonthChange,
  columnView,
  numberOfMonths,
  onSearch,
  searchLabel,
  showSearchButton = true,
  datePickerLabels,
  guestPickerLabels,
  showRate,
  rateCode,
  rateLabels,
  onRateCodeChange,
}: JourneyPickerDesktopProps) {
  const { toggle, closeAll, setRef, isOpen } = useDropdownGroup<DropdownKey>(DROPDOWN_KEYS);

  const handleRangeSelect = useCallback(
    (checkIn: string, checkOut: string) => {
      onRangeSelect(checkIn, checkOut);
      if (isSingleDate || checkOut) closeAll();
    },
    [onRangeSelect, isSingleDate, closeAll]
  );

  const handleCategoryClick = useCallback(
    (id: string) => {
      closeAll();
      onCategorySelect(id);
    },
    [closeAll, onCategorySelect]
  );

  const handleSearchClick = useCallback(() => {
    closeAll();
    onSearch();
  }, [closeAll, onSearch]);

  const handleRateApply = useCallback(
    (code: string) => {
      onRateCodeChange?.(code);
      closeAll();
    },
    [closeAll, onRateCodeChange]
  );

  const handleRateClear = useCallback(() => {
    onRateCodeChange?.('');
  }, [onRateCodeChange]);

  return (
    <Bar>
      {showCategory && (
        <CategoryWrapper ref={setRef('category')}>
          <Pill type="button" $active={isOpen('category')} onClick={() => toggle('category')}>
            <PillIcon aria-hidden>
              <CategorySearchIcon size={20} />
            </PillIcon>
            {currentCategoryLabel}
            <ChevronIcon>
              <ChevronDownIcon />
            </ChevronIcon>
          </Pill>

          {isOpen('category') && (
            <CategoryDropdown>
              {categories!.map((cat) => (
                <CategoryOption
                  key={cat.id}
                  type="button"
                  $active={cat.id === category}
                  onClick={() => handleCategoryClick(cat.id)}
                >
                  <span>{cat.label}</span>
                  <CategoryThumb>
                    {cat.imageUrl && (
                      <CategoryThumbImg src={cat.imageUrl} alt={cat.imageAlt ?? ''} />
                    )}
                  </CategoryThumb>
                </CategoryOption>
              ))}
            </CategoryDropdown>
          )}
          <DotSeparator aria-hidden>
            <DotIcon />
          </DotSeparator>
        </CategoryWrapper>
      )}

      <DatesWrapper ref={setRef('dates')} $isFirst={!showCategory}>
        <Pill
          type="button"
          $active={isOpen('dates')}
          aria-expanded={isOpen('dates')}
          data-journey-picker-edit-dates
          onClick={() => toggle('dates')}
        >
          {datesLabel}
        </Pill>

        {isOpen('dates') && (
          <DropdownAnchor>
            <DatePicker
              checkIn={fromDate || null}
              checkOut={isSingleDate ? null : toDate || null}
              singleDateMode={isSingleDate}
              availability={availability}
              isLoading={isLoadingAvailability}
              onRangeSelect={handleRangeSelect}
              onMonthChange={onMonthChange}
              columnView={columnView}
              numberOfMonths={numberOfMonths}
              labels={datePickerLabels}
            />
          </DropdownAnchor>
        )}
      </DatesWrapper>

      {displayGuests && (
        <>
          <DotSeparator aria-hidden>
            <DotIcon />
          </DotSeparator>

          {guestMode === 'combined' ? (
            <GuestsWrapper ref={setRef('guests')}>
              <Pill type="button" $active={isOpen('guests')} onClick={() => toggle('guests')}>
                {guestsLabel}
              </Pill>

              {isOpen('guests') && (
                <DropdownAnchor $align="right">
                  <GuestDropdown $asInline $withShadow>
                    <CounterRow>
                      <CounterLabel>{guestPickerLabels?.guests ?? 'Guests'}</CounterLabel>
                      <Counter
                        value={adults}
                        min={1}
                        max={20}
                        onChange={onAdultsChange}
                        decreaseLabel={guestPickerLabels?.decreaseAdults}
                        increaseLabel={guestPickerLabels?.increaseAdults}
                      />
                    </CounterRow>
                  </GuestDropdown>
                </DropdownAnchor>
              )}
            </GuestsWrapper>
          ) : (
            <GuestsWrapper ref={setRef('guests')}>
              <Pill type="button" $active={isOpen('guests')} onClick={() => toggle('guests')}>
                {guestsLabel}
              </Pill>

              {isOpen('guests') && (
                <DropdownAnchor $align="right">
                  <GuestPicker
                    adults={adults}
                    childCount={children}
                    onAdultsChange={onAdultsChange}
                    onChildrenChange={onChildrenChange}
                    renderInline
                    showShadow
                    labels={guestPickerLabels}
                  />
                </DropdownAnchor>
              )}
            </GuestsWrapper>
          )}
        </>
      )}
      {showRate && onRateCodeChange && (
        <>
          <DotSeparator aria-hidden>
            <DotIcon />
          </DotSeparator>
          <RateWrapper ref={setRef('rate')}>
            {rateCode ? (
              <RatePillGroup>
                <RatePillLabelButton type="button" onClick={() => toggle('rate')}>
                  <RateLabelText title={rateCode}>{rateCode}</RateLabelText>
                </RatePillLabelButton>
                <ClearRateButton
                  type="button"
                  aria-label={rateLabels.clear}
                  onClick={handleRateClear}
                >
                  ×
                </ClearRateButton>
              </RatePillGroup>
            ) : (
              <Pill type="button" $active={isOpen('rate')} onClick={() => toggle('rate')}>
                {rateLabels.pill}
              </Pill>
            )}
            {isOpen('rate') && (
              <DropdownAnchor $align="right">
                <RateDropdownShell>
                  <RateCodeField
                    value={rateCode}
                    placeholder={rateLabels.placeholder}
                    applyLabel={rateLabels.apply}
                    onApply={handleRateApply}
                  />
                </RateDropdownShell>
              </DropdownAnchor>
            )}
          </RateWrapper>
        </>
      )}
      {showSearchButton && (
        <SearchWrapper>
          <SearchPill type="button" onClick={handleSearchClick} aria-label={searchLabel}>
            <SearchSmallIcon size={24} />
          </SearchPill>
        </SearchWrapper>
      )}
    </Bar>
  );
}
